import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { useGetDimensionalityOptions } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useSaveValuesFromField } from 'hooks/useSaveValuesFromField';

import { FieldArrayComponent, FieldArrayTabs } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';

import { concatWithDot } from 'helpers/string';

import { AveragePricesTable } from './components/AveragePricesTable';
import { CurrentMarketStructure } from './components/CurrentMarketStructure';
import { ForecastedConsumption } from './components/ForecastedConsumption';
import { MarketTrendRow } from './components/MarketTrendRow';
import { MonthYearDatepicker } from './components/MonthYearDatePicker';
import {
  CURRENT_MARKET_STRUCTURE_NAMES,
  FORECASTED_CONSUMPTION_CHART_NAMES,
  forecastedConsumptionChartInitialValues,
  MARKET_ANALYSIS_OVERVIEW_NAMES,
  marketTrendsInitialValues,
  prefix,
} from './utils/form';
import { CurrencySelector } from '../../../components/CurrencySelector';
import {
  EXECUTIVE_SUMMARY_MARKET_NAMES,
  prefix as executiveSummaryMarketPrefix,
} from '../../ExecutiveSummary/MarketExecSummary/utils/form';

export const MarketAnalysisOverview: FC = memo(() => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const DIMENSIONALITY_OPTIONS = useGetDimensionalityOptions();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKET_ANALYSIS,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKET_ANALYSIS.MARKET_OVERVIEW,
  );

  const mappedFieldName = concatWithDot(executiveSummaryMarketPrefix, EXECUTIVE_SUMMARY_MARKET_NAMES.DYNAMICS);

  const handleSaveToMappedField = useSaveValuesFromField(mappedFieldName);

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('marketAnalysisForm.title')}
          badgeText={t('marketAnalysisForm.overview.title')}
          section={FOR_BANK_SECTIONS.MARKET_ANALYSIS}
        />
      </FormSectionCard>
      {/* 4.1.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.mainMarketOverview')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.mainMarketOverview"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.servicesProductsSummary')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.servicesProductsSummary"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.consumersDescription')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.CONSUMERS_DESCRIPTION)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.consumersDescription"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.CONSUMERS_DESCRIPTION)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.competitorsSpecification')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_COMPETITORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.competitorsSpecification"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_COMPETITORS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.5 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.consumptionDetails')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.consumptionDetails"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.6 */}
      <FieldArrayTabs
        name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.FORECASTED_CONSUMPTION_CHART)}
        buttonItemName={FORECASTED_CONSUMPTION_CHART_NAMES.TITLE}
        title={t('marketAnalysisForm.overview.forecastedConsumption')}
        buttonText={t('chart')}
        removeButtonText={t('removeChart')}
        initialItem={{
          ...forecastedConsumptionChartInitialValues,
          [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: t('chart'),
        }}
        disabled={!canAccess}
      >
        {(namePrefix) => <ForecastedConsumption name={namePrefix} disabled={!canAccess} />}
      </FieldArrayTabs>
      {/* 4.1.7 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.marketShare')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_SHARE)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.marketShare"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_SHARE)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
          onBlur={handleSaveToMappedField}
        />
      </FormSectionCard>
      {/* 4.1.8 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.marketTrends.title')}
        collapsible
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS)}
        aiFieldType="complex"
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS)}
          initialItem={marketTrendsInitialValues}
          buttonText={t('trend')}
          disabled={!canAccess}
        >
          {(namePrefix) => <MarketTrendRow name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.1.9 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.salesMethod')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.salesMethod"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.10 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.marketStructureDescription')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.marketStructureDescription"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.11 */}
      <FieldArrayTabs
        name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.CURRENT_MARKET_STRUCTURE)}
        buttonItemName={CURRENT_MARKET_STRUCTURE_NAMES.TITLE}
        title={t('marketAnalysisForm.overview.indicateMarketStructure')}
        buttonText={t('chart')}
        removeButtonText={t('removeChart')}
        initialItem={{
          ...forecastedConsumptionChartInitialValues,
          [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: t('chart'),
        }}
        disabled={!canAccess}
      >
        {(namePrefix) => <CurrentMarketStructure name={namePrefix} disabled={!canAccess} />}
      </FieldArrayTabs>
      {/* 4.1.12 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.marketGrowthFactors')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.marketGrowthFactors"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.13 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.technologyImpact')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.TECHNOLOGY_IMPACT)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.technologyImpact"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.TECHNOLOGY_IMPACT)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.14 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.overview.priceStrategies')}
        aiFieldName={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES)}
      >
        <TextInputControl
          multiline
          control={control}
          id="marketAnalysisForm.overview.priceStrategies"
          name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.1.15 */}
      <FormSectionCard disabled={!canAccess} title={t('marketAnalysisForm.overview.averagePrices')} collapsible>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextInputControl
              control={control}
              id="marketAnalysisForm.overview.priceStrategies"
              name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_TITLE)}
              label={t('chartTitle')}
              disabled={!canAccess}
              maxLength={150}
            />
          </Grid>
          <Grid container justifyContent="space-between" alignItems="center" item>
            <MonthYearDatepicker
              name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_DATE)}
              disabled={!canAccess}
            />
            <CurrencySelector
              name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CURRENCY)}
              defaultValue={DIMENSIONALITY_OPTIONS[0].value}
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12}>
            <FormSectionFilledCard>
              <AveragePricesTable
                disabled={!canAccess}
                name={concatWithDot(prefix, MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CHART)}
              />
            </FormSectionFilledCard>
          </Grid>
        </Grid>
      </FormSectionCard>
    </>
  );
});
